import { AccessProvider, EVERYONE_FEATURES, useAccess, useFeature, useIsAdmin, type Access, type FeatureKey } from './parts/access.js';
import { endInheritedView, onViewElsewhere, useViewing, VIEW_MARKER } from './view-as.js';
import { KeeperViewBand, KeeperViewChooser, KeeperViewProvider, mayBeKeeper } from './rails/KeeperView.js';
import { SignIn } from './onboarding/intro/SignIn.js';
import { routeId } from './route-id.js';
import { Component, useCallback, useEffect, useState, useSyncExternalStore, type ErrorInfo, type ReactNode } from 'react';
import { api, Unauthenticated, type Me, type OnboardingState, type Store } from './api.js';
import { ItemPage } from './ItemPage.js';
import { LoadingScreen } from './LoadingScreen.js';
import { Brand, Logo } from './Logo.js';
import { LogoutButton } from './LogoutButton.js';
import { OnboardingGate } from './onboarding/Onboarding.js';
import { buildStamp, count } from './labels.js';
import { useNewBuild } from './new-build.js';
import { useQuestions } from './Questions.js';
import { Policy } from './Policy.js';
import { Industry } from './Industry.js';
import { Meetings } from './Meetings.js';
import { AdminConsole } from './admin/AdminConsole.js';
import { Usage } from './usage/Usage.js';
import { Engineering } from './engineering/Engineering.js';
import { MomentumPage } from './momentum/MomentumPage.js';
import { LiveOps } from './liveops/LiveOps.js';
import { Recent } from './Recent.js';
import { ThreadPage } from './threads/ThreadPage.js';
import { Triage } from './Triage.js';
import { Companies } from './records/Companies.js';
import { CompanyRecord } from './records/CompanyRecord.js';
import { Contracts } from './records/Contracts.js';
import { Deals } from './records/Deals.js';
import { CompanyIntel } from './intel/CompanyIntel.js';
import { Matters } from './records/Matters.js';
import { People } from './records/People.js';
import { Rights } from './records/Rights.js';
import { personPath, personReadPath, SimpleRecord } from './records/shared.js';
import { MatterContents } from './records/MatterContents.js';
import { TasksPage } from './tasks/TasksPage.js';
import { TwilioTest } from './twilio/TwilioTest.js';
import { useChatUnread } from './Messenger.js';
import { ChatsPage } from './chats/ChatsPage.js';
import { NavUnread } from './NavUnread.js';
import { MessengerGuest } from './MessengerGuest.js';
import { CopilotPanel } from './copilot/Panel.js';
import { CalendarPage } from './calendar/CalendarPage.js';
import { PlaybooksPage } from './playbooks/PlaybooksPage.js';
import { SearchBox } from './find/SearchBox.js';
import { SearchPage } from './find/SearchPage.js';
import { useDocumentTitle } from './title.js';   // BUGS.md D192: the tab names the page
import { reportZone } from './zone.js';
import './messenger.css';
import { ActionButton } from './parts/index.js';
import { AdminRouter, isKeeperArea, type AdminSurface } from './admin/master/shell/AdminRouter.js';
import { adminRedirect } from './admin/master/shell/redirects.js';
import { Refused } from './admin/master/states/States.js';

type Phase =
  | { kind: 'booting' }
  | { kind: 'signed_out' }
  | { kind: 'no_store'; me: Me }
  | { kind: 'ready'; me: Me; store: Store; state: OnboardingState }
  | { kind: 'error'; message: string };

const POLL_MS = 5000;

/**
 * Routing (B48): a path per page, pushState to move and popstate to follow the back
 * button. The record pages add one parameter, the row's id, parsed by hand (B165); still
 * no router library. Milestone 4 (B181) adds the item page, `/items/:id` with the store it
 * came from in the query (the blended list holds two stores, D12, D43), and "Everything
 * read" at `/read`, reached from the bar and the records and from no menu entry (U18).
 */
export type Surface =
  | { page: 'home' } | { page: 'admin'; area?: NonNullable<AdminSurface['area']>; id?: string; person?: string; group?: string } | { page: 'rails' } | { page: 'policy' }
  // BUGS.md D78: /read?person=<id>&store=<tenant> is one person's items, from their page.
  | { page: 'read'; person?: string; store?: string } | { page: 'usage' } | { page: 'engineering' } | { page: 'operations' } | { page: 'momentum' }
  | { page: 'item'; id: string; store: string | null }
  | { page: 'thread'; id: string; store: string | null }
  // BUGS.md D74: a person's page names its store (?store=) when the person is the personal store's.
  | { page: 'companies'; id?: string } | { page: 'tasks'; id?: string } | { page: 'people'; id?: string; store?: string } | { page: 'deals'; id?: string }
  | { page: 'matters'; id?: string } | { page: 'contracts'; id?: string } | { page: 'rights'; id?: string }
  | { page: 'playbooks' }
  | { page: 'intel-company' } | { page: 'intel-industry' }
  | { page: 'twilio-test' }
  | { page: 'chats'; id?: string }
  | { page: 'meetings'; id?: string }
  | { page: 'calendar' }
  | { page: 'search' };
type Page = Surface['page'];

/** The existing menu order and words, with access decided by C1 features or administration. */
export type MenuEntry = { page: Page; label: string; path: string } & (
  | { feature: FeatureKey; alternative?: FeatureKey; adminOnly?: never; keeperOnly?: never }
  | { feature?: never; alternative?: never; adminOnly: true; keeperOnly?: never }
  | { feature?: never; alternative?: never; adminOnly?: never; keeperOnly: true }
);
export const MENU: Array<{ heading?: string; entries: MenuEntry[] }> = [
  // U91, by Mike: Calendar is a fifth item here, after Meetings. Meetings is what happened; Calendar is
  // what is coming, and a meeting on it opens into the co-pilot's brief.
  { entries: [{ page: 'home', feature: 'page.triage', label: 'Triage', path: '/' }, { page: 'tasks', feature: 'page.tasks', label: 'Tasks', path: '/tasks' }, { page: 'chats', feature: 'page.chats', label: 'Chats', path: '/chats' }, { page: 'meetings', feature: 'page.meetings', label: 'Meetings', path: '/meetings' }, { page: 'calendar', feature: 'page.calendar', label: 'Calendar', path: '/calendar' }] },
  { heading: 'Records', entries: [
    { page: 'companies', feature: 'page.companies', label: 'Companies', path: '/companies' }, { page: 'people', feature: 'page.people', label: 'People', path: '/people' }, { page: 'deals', feature: 'page.deals', label: 'Deals', path: '/deals' }, { page: 'momentum', feature: 'page.momentum.team', label: 'Momentum', path: '/momentum', alternative: 'page.momentum.own' },
    { page: 'matters', feature: 'page.matters', label: 'Matters', path: '/matters' }, { page: 'contracts', feature: 'page.contracts', label: 'Contracts', path: '/contracts' }, { page: 'rights', feature: 'page.rights', label: 'Rights', path: '/rights' },
    { page: 'playbooks', feature: 'page.playbooks', label: 'Playbooks', path: '/playbooks' },   // module 13, artboard F: after Rights; in everyone's menu since the rebuilt page (U257, pipeline K3)
  ] },
  { heading: 'Intelligence', entries: [{ page: 'intel-company', feature: 'page.intel.company', label: 'Company', path: '/intelligence/company' }, { page: 'intel-industry', feature: 'page.intel.industry', label: 'Industry', path: '/intelligence/industry' }, { page: 'operations', feature: 'page.operations', label: 'Operations', path: '/operations' }] },
  { heading: 'Running Cobrain', entries: [{ page: 'admin', label: 'Admin', path: '/admin', adminOnly: true }, { page: 'engineering', feature: 'page.engineering', label: 'Engineering', path: '/engineering' }] },
];

const POLICY_ENTRY: MenuEntry = { page: 'policy', feature: 'page.policy', label: 'Policy', path: '/policy' };

const RECORD_PAGES = new Set<Page>(['companies', 'tasks', 'people', 'deals', 'momentum', 'matters', 'contracts', 'rights']);
/**
 * Kept only for the imports wave 2 removes. Every Page is in the look (OX8); this never decides a class. A record
 * rather than a list, so the compiler refuses it the day a Page is added and not named here.
 */
const EVERY_PAGE: Record<Page, true> = {
  home: true, item: true, thread: true, read: true, tasks: true, chats: true, meetings: true, calendar: true, search: true,
  companies: true, people: true, deals: true, momentum: true, matters: true, contracts: true, rights: true, playbooks: true,
  'intel-company': true, 'intel-industry': true, operations: true,
  admin: true, rails: true, usage: true, engineering: true, policy: true, 'twilio-test': true,
};
export const MATCH_CENTRE = new Set<Page>(Object.keys(EVERY_PAGE) as Page[]);

// MA5: one segment each (brief 8); AdminRouter's union names the same seven. Declared here, not imported, because
// admin-map-source.ts lifts surfaceOf out of this file alone.
const ADMIN_AREA_PAGES = new Set<string>(['sources', 'privacy', 'spend', 'decides', 'policy', 'vault', 'powers']);

export function surfaceOf(pathname: string, search = ''): Surface {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { page: 'home' };
  if (parts[0] === 'admin' && parts.length === 1) return { page: 'admin' };
  // MA5: Sources, Privacy rules and the keeper's five areas, one segment each (brief 8).
  if (parts[0] === 'admin' && parts.length === 2 && ADMIN_AREA_PAGES.has(parts[1]!)) return { page: 'admin', area: parts[1] as NonNullable<AdminSurface['area']> };
  if (parts[0] === 'admin' && parts[1] === 'people') {
    if (parts.length === 2) return { page: 'admin', area: 'people' };
    if (parts.length === 4 && (parts[2] === 'group' || parts[2] === 'person')) return { page: 'admin', area: parts[2], id: decodeURIComponent(parts[3]!) };
    if (parts.length === 3 && parts[2] === 'add') return { page: 'admin', area: 'add' };
    if (parts.length === 3 && parts[2] === 'changes') {
      const q = new URLSearchParams(search);
      return { page: 'admin', area: 'changes', ...(q.get('person') ? { person: q.get('person')! } : {}), ...(q.get('group') ? { group: q.get('group')! } : {}) };
    }
  }
  if (parts[0] === 'rails') return { page: 'rails' };
  if (parts[0] === 'policy') return { page: 'policy' };
  if (parts[0] === 'engineering') return { page: 'engineering' };   // what the development team is shipping, the super admin's (U89)
  if (parts[0] === 'momentum') return { page: 'momentum' };
  if (parts[0] === 'operations') return { page: 'operations' };   // live operations, one named person's (module 14, U103, B800)
  if (parts[0] === 'usage') return { page: 'usage' };            // the live usage monitor, the super admin's (U70)
  if (parts[0] === 'read') {
    const q = new URLSearchParams(search);
    const person = q.get('person');
    const store = q.get('store');
    return { page: 'read', ...(person ? { person } : {}), ...(person && store ? { store } : {}) };
  }
  if (parts[0] === 'people' && parts[1]) {
    const store = new URLSearchParams(search).get('store');
    return { page: 'people', id: parts[1], ...(store ? { store } : {}) };
  }
  if (parts[0] === 'items' && parts[1]) return { page: 'item', id: routeId(parts[1]), store: new URLSearchParams(search).get('store') };
  if (parts[0] === 'threads' && parts[1]) return { page: 'thread', id: routeId(parts[1]), store: new URLSearchParams(search).get('store') };   // REQ-2026-10-07-167
  if (parts[0] === 'intelligence') return parts[1] === 'industry' ? { page: 'intel-industry' } : { page: 'intel-company' };
  // U22: /meetings and /meetings/:id. Not a record page — a meeting is something the person was
  // in, not a row of the business, and the list is theirs rather than the store's.
  if (parts[0] === 'meetings') return parts[1] ? { page: 'meetings', id: routeId(parts[1]) } : { page: 'meetings' };
  if (parts[0] === 'calendar') return { page: 'calendar' };   // U91: the person's own week, and a meeting's brief
  if (parts[0] === 'search') return { page: 'search' };   // U227, F3: everything a search finds, the words and the tab in the query
  if (parts[0] === 'playbooks') return { page: 'playbooks' };   // module 13 L12, artboard F: one page, the products as tabs on it
  if (parts[0] === 'chats') return parts[1] ? { page: 'chats', id: routeId(parts[1]) } : { page: 'chats' };   // module 09 M11, surface 7
  if (parts[0] === 'admin' && parts[1] === 'twilio-test') return { page: 'twilio-test' };   // B1: the softphone recording test (B333)
  if (parts[0] === 'deals' && parts[1] === 'shows' && parts[2]) return { page: 'deals', id: `shows/${routeId(parts[2])}` };   // U160: a show's day sheet, drawn by the Deals page (track show)
  if (RECORD_PAGES.has(parts[0] as Page)) return parts[1] ? { page: parts[0] as Page, id: parts[1] } as Surface : { page: parts[0] as Page } as Surface;
  return { page: 'home' };
}

export function pathOf(s: Surface): string {
  if (s.page === 'admin' && s.area) {
    if (s.area === 'person' || s.area === 'group') return `/admin/people/${s.area}/${encodeURIComponent(s.id!)}`;
    if (s.area === 'people') return '/admin/people';
    if (ADMIN_AREA_PAGES.has(s.area)) return `/admin/${s.area}`;
    const q = new URLSearchParams(); if (s.person) q.set('person', s.person); if (s.group) q.set('group', s.group);
    return `/admin/people/${s.area}${q.size ? `?${q}` : ''}`;
  }
  if (s.page === 'read') return s.person ? personReadPath(s.person, s.store) : '/read';
  if (s.page === 'people' && s.id) return personPath(s.id, s.store);
  if (s.page === 'search') return '/search';
  if (s.page === 'policy') return '/policy';
  if (s.page === 'rails') return '/rails';
  if (s.page === 'item') return `/items/${encodeURIComponent(s.id)}${s.store ? `?store=${encodeURIComponent(s.store)}` : ''}`;
  if (s.page === 'thread') return `/threads/${encodeURIComponent(s.id)}${s.store ? `?store=${encodeURIComponent(s.store)}` : ''}`;
  for (const g of MENU) for (const e of g.entries) if (e.page === s.page) return 'id' in s && s.id ? `${e.path}/${s.id}` : e.path;
  return '/';
}

/** One caller applies redirects on initial load, navigation and browser back/forward. */
function surfaceFromLocation(): Surface {
  const redirect = adminRedirect(window.location.pathname, window.location.search);
  if (redirect) window.history.replaceState(window.history.state, '', redirect);
  return surfaceOf(window.location.pathname, window.location.search);
}

export function useSurface(): [Surface, (path: string) => void] {
  const [surface, setSurface] = useState<Surface>(surfaceFromLocation);
  useEffect(() => {
    const onPop = () => setSurface(surfaceFromLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  // `replace` for an address the page settles on by itself (Chats opening its newest conversation, BUGS.md M56), so
  // Back does not land on the bare address and get sent forward again.
  const go = useCallback((path: string, opts?: { replace?: boolean }) => {
    if (opts?.replace) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
    setSurface(surfaceFromLocation());
    window.scrollTo(0, 0);
  }, []);
  return [surface, go];
}

/** Menu headings use the same feature answer as their entries; loading exposes only C1's everyone-pages. */
function visibleEntries(entries: MenuEntry[], access: Access | null, admin: boolean, viewing: boolean): MenuEntry[] {
  return entries.filter(e => e.adminOnly ? !viewing && admin
    : e.keeperOnly ? !viewing && access?.person?.keeper === true
    : access?.features ? access.features.includes(e.feature) || (!!e.alternative && access.features.includes(e.alternative))
    : EVERYONE_FEATURES.includes(e.feature));
}

// An exhaustive route map keeps every feature page and its detail addresses behind the same gate.
const PAGE_FEATURES: Record<Page, FeatureKey | null> = {
  home: 'page.triage', tasks: 'page.tasks', chats: 'page.chats', meetings: 'page.meetings', calendar: 'page.calendar',
  companies: 'page.companies', people: 'page.people', deals: 'page.deals', momentum: 'page.momentum.team',
  matters: 'page.matters', contracts: 'page.contracts', rights: 'page.rights', playbooks: 'page.playbooks',
  'intel-company': 'page.intel.company', 'intel-industry': 'page.intel.industry', operations: 'page.operations',
  usage: 'page.usage', engineering: 'page.engineering', policy: 'page.policy',
  item: null, read: null, thread: null, search: null, admin: null, rails: null, 'twilio-test': null,
};

export function PageAccessGate({ page, children }: { page: Page; children: ReactNode }) {
  const access = useAccess();
  const viewing = useViewing();
  const feature = PAGE_FEATURES[page];
  const granted = useFeature(feature ?? 'page.triage');
  const ownMomentum = useFeature('page.momentum.own');
  const admin = useIsAdmin();
  if (page === 'admin' || page === 'rails' || page === 'twilio-test') {
    if (viewing || access?.viewing) return <Refused words="The admin pages are not shown while you view as someone." />;
    if (!access) return null;
    return admin ? children : <Refused words="The admin pages are an admin's." />;
  }
  if (!feature) return children;
  if (!access) return null;
  const allowed = granted || (page === 'momentum' && ownMomentum);
  return allowed ? children : <p className="plain pad">This is not part of your access. An admin sets access in Admin.</p>;
}

function MenuFeature({ entry, children }: { entry: MenuEntry; children: ReactNode }) {
  const access = useAccess();
  const granted = useFeature(entry.feature ?? 'page.triage');
  const alternative = useFeature(entry.alternative ?? 'page.triage');
  if (!entry.feature) return children; // Administration was checked by the menu group.
  return (access ? granted || (!!entry.alternative && alternative) : EVERYONE_FEATURES.includes(entry.feature)) ? children : null;
}

const ICONS: Record<string, ReactNode> = {
  chats: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H8l-4 4z" /></svg>,
  home: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h10M4 18h6" /></svg>,
  tasks: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 11l2 2 4-4" /><rect x="4" y="4" width="16" height="16" rx="3" /></svg>,
  companies: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V6l8-3v17M12 20V10l8 2v8" /></svg>,
  people: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></svg>,
  momentum: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17l5-5 4 3 8-10M15 5h5v5" /></svg>,
  deals: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 17l5-5 4 4 7-8" /><path d="M14 8h6v6" /></svg>,
  matters: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h10l4 4v12H5z" /><path d="M9 13h6M9 17h6" /></svg>,
  contracts: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h9l4 4v14H6z" /><path d="M9 15l2 2 4-4" /></svg>,
  rights: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18" /></svg>,
  'intel-company': <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19h16" /><path d="M6 15l4-5 4 3 4-6" /></svg>,
  'intel-industry': <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h12l4 4v12H4z" /><path d="M8 12h8M8 16h8" /></svg>,
  usage: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20V12M10 20V6M15 20v-5M20 20V9" /></svg>,
  engineering: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 7l-5 5 5 5M16 7l5 5-5 5M13.5 5l-3 14" /></svg>,
  operations: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h4l2-5 3 10 2-5h7" /></svg>,
  rails: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></svg>,
  policy: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h9l3 3v15H6z" /><path d="M9 10h6M9 14h6M9 18h3" /></svg>,
  meetings: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H4z" /><path d="M9 20h6M12 16v4" /></svg>,
  calendar: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  playbooks: <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5a2 2 0 0 1 2-2h12v15H7a2 2 0 0 0-2 2z" /><path d="M5 20a2 2 0 0 0 2 2h12v-4" /></svg>,
};

/** A page whose tab title is longer than its word in the rail (D192): the rail says "Company" under Intelligence. */
const PAGE_TITLES: Partial<Record<Page, string>> = { 'intel-company': 'Company intelligence' };

/** The navy rail (U20), the menu exactly as parts/nav.sh renders it. */
export function Nav({ store, surface, go }: { store: Store; surface: Surface; go: (path: string) => void }) {
  const chatUnread = useChatUnread(store.tenantId);
  const viewing = useViewing();
  const access = useAccess();
  const admin = useIsAdmin();
  // A thread is opened from a company's record, so Companies stays lit (REQ-2026-10-07-167).
  const current: Page = surface.page === 'item' || surface.page === 'read' ? 'home' : surface.page === 'thread' ? 'companies' : surface.page === 'rails' ? 'admin' : surface.page;
  // U31: on a phone, in the Match-centre look, the rail is a bottom bar of the first four and More, which opens the rest.
  const [more, setMore] = useState(false);
  const pick = (path: string) => { setMore(false); go(path); };
  // On a phone the bar shows the first group and More; every other surface -- the six records
  // tables, the two intelligence pages, admin -- lives behind More. Without this, none of them
  // marks where you are at phone width, because the entry that would carry the blue is not drawn.
  // /search is in no menu at all (the bar's box opens it), so nothing is marked for it.
  const behindMore = current !== 'search' && !MENU[0]!.entries.some((e) => e.page === current);
  // BUGS.md D192: the tab is titled with the page the rail marks, "<page> - Cobrain" (title.ts).
  useDocumentTitle(current === 'search' ? 'Search' : PAGE_TITLES[current] ?? [...MENU.flatMap((g) => g.entries), POLICY_ENTRY].find((e) => e.page === current)?.label);
  return (
    // BUGS.md D192: the rail is the site's navigation, a nav landmark, and its group names are labels, not headings, so
    // nothing comes before a page's own h1 in the outline. Drawn as before: the classes carry every rule (styles.css).
    <nav className={more ? 'nav open' : 'nav'} aria-label="Menu">
      {MENU.map((g, i) => {
        const shown = visibleEntries(g.entries, access, admin, !!viewing || !!access?.viewing);
        // A heading with nothing under it for this reader is not drawn either.
        return shown.length === 0 ? null : (
          <div key={i} style={{ display: 'contents' }}>
            {g.heading ? <div className="nav-h">{g.heading}</div> : null}
            {shown.map((e) => (
              <MenuFeature key={e.page} entry={e}><ActionButton reading type="button" className={`${current === e.page ? 'n on' : 'n'}${i === 0 && !(current === 'admin' && e.page === 'calendar') ? ' primary' : ''}`} aria-current={current === e.page ? 'page' : undefined} onClick={() => pick(e.path)}>
                {ICONS[e.page === 'admin' ? 'rails' : e.page] ?? null}{e.label}{e.page === 'chats' ? <NavUnread n={chatUnread} /> : null}
              </ActionButton></MenuFeature>
            ))}
          </div>
        );
      })}
      <MenuFeature entry={POLICY_ENTRY}><ActionButton reading type="button" className={`n admin-policy-foot${current === 'policy' ? ' on' : ''}`} aria-current={current === 'policy' ? 'page' : undefined} onClick={() => pick(POLICY_ENTRY.path)}>
        {ICONS.policy}Policy
      </ActionButton></MenuFeature>
      <ActionButton reading type="button" className={behindMore && !more ? 'n more on' : 'n more'} aria-expanded={more} aria-current={behindMore && !more ? 'page' : undefined} onClick={() => setMore((v) => !v)}>
        <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h.01M12 12h.01M19 12h.01" /></svg>{more ? 'Close' : 'More'}
      </ActionButton>
    </nav>
  );
}

// The lockup is drawn at 40 beside the rail and at 30 on a phone, where the rail is not beside the bar and the phone
// block in styles.css was measured against 30 (B1954). matchMedia is guarded: jsdom, which the render tests use, has none.
const BAR_NARROW = '(max-width: 760px)';
function useBarNarrow(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const m = window.matchMedia(BAR_NARROW);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(BAR_NARROW).matches,
    () => false,
  );
}

function CopilotAccess({ me }: { me: Me }) {
  const access = useAccess();
  return <KeeperViewProvider asks={mayBeKeeper(me, access)}><div className="mc">{me.viewing ? <KeeperViewBand view={me.viewing} /> : null}<CopilotPanel /></div></KeeperViewProvider>;
}

const PERSONAL_KEY = 'cobrain.personal';
function readPersonal(): boolean { try { return localStorage.getItem(PERSONAL_KEY) === 'on'; } catch { return false; } }

/**
 * The bar every signed-in surface carries (parts/bar.html): the brand, whatever that
 * surface wants in the middle (the question count), who and which store, the personal
 * toggle (U3: "Personal on" blends the personal store into the same list, private items
 * in violet; it replaces the store switch of B72), the build stamp.
 */
function Bar({ me, store, middle, search, onBrand, personal, onPersonal, go, compact = false }: { me: Me; store: Store; middle?: ReactNode; search?: ReactNode; onBrand?: () => void; personal: boolean; onPersonal: (v: boolean) => void; go: (path: string) => void; compact?: boolean }) {
  const hasPersonal = me.stores.some((s) => s.kind === 'personal');
  const narrow = useBarNarrow();
  return (
    // `rail-bar`: this is the bar above the rail, whose brand fills the rail's width so the search box starts where the
    // page does (styles.css; Mike, 5 Oct 2026). The guest bar has no rail and keeps the plain `.bar-top`.
    <header className="bar-top rail-bar">
      <a className="brand" href="/" onClick={(e) => { e.preventDefault(); onBrand?.(); go('/'); }}>
        <Brand size={compact ? 22 : narrow ? 30 : 40} />
      </a>
      {/* The search box, between the logo and the name, on every page (U227, F3). */}
      {search}
      {middle}
      <span className="who who-bar">
        {/* Named so the phone rule can drop them (styles.css, the bar's own phone block): at 375 the bar ran past the viewport. */}
        <span className="who-name">{me.login.displayName.split(' ')[0]}</span><KeeperViewChooser /><span className="who-store">{store.kind === 'commercial' ? 'Commercial' : 'Personal'}</span>
        {hasPersonal && !me.viewing ? (
          <button type="button" className={personal ? 'toggle on' : 'toggle'} aria-pressed={personal} onClick={() => onPersonal(!personal)}>
            Personal {personal ? 'on' : 'off'} <span className="knob" />
          </button>
        ) : null}
      </span>
      {/* Which build is on the screen (B65). Small, muted and always there, because the
          alternative is reading a server log to settle whether a deploy arrived. */}
      <span className="build" title={`build ${__BUILD_TIME__}`}>{buildStamp(__BUILD_TIME__)}</span>
      <LogoutButton />
    </header>
  );
}

/**
 * The signed-in surface. Its own component so the questions hook is never called
 * conditionally, and so the bar can carry the count while the card is folded.
 */
/**
 * A render fault in one page must not blank the whole app (found live 2026-09-16: a missing field in the draft
 * panel unmounted every page, and the person could not get away from a plain background). The boundary keeps
 * the rail and the bar, says what failed in words, and offers a reload.
 */
class PageBoundary extends Component<{ children: ReactNode; surfaceKey: string }, { message: string | null }> {
  override state = { message: null as string | null };
  static getDerivedStateFromError(e: unknown) { return { message: e instanceof Error ? e.message : String(e) }; }
  override componentDidCatch(e: unknown, info: ErrorInfo) { console.error('cobrain: a page failed to render', e, info.componentStack); }
  override componentDidUpdate(prev: { surfaceKey: string }) { if (prev.surfaceKey !== this.props.surfaceKey && this.state.message) this.setState({ message: null }); }
  override render() {
    if (this.state.message) {
      return (
        <div className="panel">
          <h2>This page failed to draw</h2>
          <p className="plain">Cobrain hit a fault drawing it: {this.state.message}</p>
          <p className="note">Nothing was sent or changed by the fault. <button type="button" className="dotted" onClick={() => window.location.reload()}>Reload the page</button></p>
        </div>
      );
    }
    return this.props.children;
  }
}

function Shell({ me, store, surface: asked, go }: { me: Me; store: Store; surface: Surface; go: (path: string) => void }) {
  const access = useAccess();
  const viewingNow = useViewing();
  // Brief 5: for anyone but the keeper the keeper's addresses fall through to Triage, as any unknown address does; under
  // a view they stay admin addresses, so the gate answers "The admin pages are not shown while you view as someone."
  const surface: Surface = asked.page === 'admin' && isKeeperArea(asked.area) && access && !access.viewing && !viewingNow
    && access.person?.keeper !== true ? { page: 'home' } : asked;
  const triage = useFeature('page.triage');
  const questions = useQuestions(store, surface.page === 'home' && triage);
  // REQ-2026-09-25-10 (B1853): a page left open says when a new build is out, and reloads only when Reload is pressed.
  const updated = useNewBuild();
  const [personal, setPersonal] = useState(readPersonal);
  const onPersonal = (v: boolean) => { setPersonal(v); try { localStorage.setItem(PERSONAL_KEY, v ? 'on' : 'off'); } catch { /* the toggle still works for the session */ } };
  const personalStore = me.stores.find((s) => s.kind === 'personal') ?? null;
  const stores = personal && personalStore ? [store, personalStore] : [store];

  // The "N questions" pill: the card sits at the top of the ten (U18), so the pill goes there.
  const toQuestions = () => {
    go('/');
    window.setTimeout(() => { window.dispatchEvent(new Event('cobrain:questions')); document.querySelector('[data-item="questions"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, 50);
  };

  const wide = RECORD_PAGES.has(surface.page) || surface.page === 'search' || surface.page === 'admin' || surface.page === 'rails' || surface.page === 'item' || surface.page === 'read' || surface.page === 'usage' || surface.page === 'engineering' || surface.page === 'intel-industry' || surface.page === 'chats' || surface.page === 'meetings' || surface.page === 'operations' || surface.page === 'playbooks';
  const props = { store, open: go };
  let body: ReactNode;
  switch (surface.page) {
    case 'home': body = <Triage stores={stores} questions={questions} go={go} />; break;
    case 'item': body = <ItemPage key={`${surface.store ?? store.tenantId}:${surface.id}`} stores={me.stores} id={surface.id} storeId={surface.store} go={go} />; break;
    case 'thread': body = <ThreadPage key={`${surface.store ?? store.tenantId}:${surface.id}`} store={store} id={surface.id} storeId={surface.store} open={go} />; break;   // REQ-2026-10-07-167: a conversation read whole
    // BUGS.md D78: one person's items are read in the store that holds them, whether or not Personal is on.
    case 'read': body = surface.person
      ? <Recent stores={[me.stores.find((s) => s.tenantId === surface.store) ?? store]} person={surface.person} />
      : <Recent stores={stores} />; break;
    case 'admin': body = <AdminRouter surface={surface} />; break;
    case 'rails': body = <AdminConsole key={window.location.search} store={store} />; break;
    case 'policy': body = <Policy store={store} />; break;
    case 'engineering': body = <Engineering />; break;   // U89
    // module 14, U103: the route refuses in words too, so a typed address is turned away the same as a hidden entry.
    case 'operations': body = <LiveOps />; break;
    case 'usage': body = <Usage />; break;   // U70
    case 'chats': body = <ChatsPage store={store} {...(surface.id ? { id: surface.id } : {})} go={go} />; break;   // module 09 M11 and M16, surface 7 (B344-B349, B599): Cobrain chats and Teams in one list
    case 'twilio-test': body = <TwilioTest />; break;
    case 'companies': body = surface.id ? <CompanyRecord {...props} id={surface.id} /> : <Companies {...props} />; break;
    // Milestone 12 (U44 to U51) and U80: the landing page is the master list; /tasks/structure is the areas and the
    // directory behind it; /tasks/board, /tasks/calendar and /tasks/team are the other looks,
    // /tasks/<id> one task opened as a page.
    case 'tasks': {
      const look = surface.id === 'structure' || surface.id === 'board' || surface.id === 'calendar' || surface.id === 'team' ? surface.id : 'list';
      body = <TasksPage stores={stores} go={go} look={look} openId={surface.id && look === 'list' ? surface.id : undefined} />;
      break;
    }
    // BUGS.md D74: with Personal on, People lists both stores (two reads, merged here; D12, D43), and a person's page reads
    // the store its address names.
    case 'people': body = surface.id
      ? <SimpleRecord store={me.stores.find((s) => s.tenantId === surface.store) ?? store} page="people" id={surface.id} title="People" back="/people" open={go} />
      : <People stores={stores} open={go} />; break;
    case 'momentum': body = <MomentumPage key={props.store.tenantId} {...props} />; break;
    case 'deals': body = <Deals {...props} id={surface.id} />; break;
    // BUGS.md T24: a matter's page lists its commitments, mail, tasks and deal under its fields.
    case 'matters': body = surface.id ? <><SimpleRecord store={store} page="matters" id={surface.id} title="Matters" back="/matters" open={go} /><MatterContents store={store.tenantId} id={surface.id} open={go} /></> : <Matters {...props} />; break;
    case 'contracts': body = surface.id ? <SimpleRecord store={store} page="contracts" id={surface.id} title="Contracts" back="/contracts" open={go} /> : <Contracts {...props} />; break;
    case 'rights': body = surface.id ? <SimpleRecord store={store} page="rights" id={surface.id} title="Rights" back="/rights" open={go} /> : <Rights {...props} />; break;
    case 'intel-company': body = <CompanyIntel go={go} />; break;   // Company intelligence (module 10 U52 to U59, B517)
    case 'intel-industry': body = <Industry store={store} path={window.location.pathname} go={go} />; break;   // milestone 6 checks 3 and 4 (U9, U10, U11)
    case 'calendar': body = <CalendarPage store={store} />; break;   // U91: the week ahead, and a meeting's brief
    case 'meetings': body = <Meetings {...(surface.id ? { id: surface.id } : {})} go={go} store={store.tenantId} />; break;   // U22, T59: the calls this person was in, and what Cobrain made of each
    case 'playbooks': body = <PlaybooksPage store={store} open={go} />; break;   // the one list of objections (module 10 U244 to U257, pipeline K3)
    case 'search': body = <SearchPage stores={stores} go={go} />; break;   // U227, F3: everything a search finds
  }

  // U33: Cmd-K or Ctrl-K asks from anywhere; the ask bar lives on triage (D74), so it goes there and focuses the bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') || surface.page === 'home') return;
      e.preventDefault();
      go('/');
      window.setTimeout(() => document.querySelector<HTMLInputElement>('.askbar input')?.focus(), 60);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [surface.page, go]);

  return (
    <KeeperViewProvider asks={mayBeKeeper(me, access)}><div className={`app mc${surface.page === 'momentum' ? ' momentum-shell' : ''}`}>
      <Bar
        me={me} store={store} go={go} personal={personal} onPersonal={onPersonal}
        search={<SearchBox stores={stores} go={go} />}
        middle={questions.left > 0 ? <button className="ask-open" onClick={toQuestions}>{count(questions.left, 'question')}</button> : null}
      />
      {me.viewing ? <KeeperViewBand view={me.viewing} /> : null}
      {updated ? <div className="updated" role="status">Cobrain has been updated. <button type="button" className="dotted" onClick={() => window.location.reload()}>Reload</button></div> : null}
      <div className="shell">
        <Nav store={store} surface={surface} go={go} />
        <main className={wide ? 'page wide' : 'page'}><PageBoundary surfaceKey={window.location.pathname}><PageAccessGate page={surface.page}>{body}</PageAccessGate></PageBoundary></main>
      </div>
    </div></KeeperViewProvider>
  );
}

export function App() {
  if (window.location.pathname === '/guest') return <MessengerGuest />;   // a guest has no Microsoft session (M3, B347)
  const [phase, setPhase] = useState<Phase>({ kind: 'booting' });
  const [tick, setTick] = useState(0);
  const [surface, go] = useSurface();

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let checkingView = false;
    const load = async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        if (await endInheritedView(me.viewing, api.endViewAs, () => window.location.reload())) return;
        if (me.viewing) {
          const store = me.stores[0];
          if (!store) throw new Error('The viewed store is unavailable.');
          if (!cancelled) setPhase({ kind: 'ready', me, store, state: { status: 'ready', connectorId: null, connectedAt: null, expectedItems: null, doneItems: 0, startedAt: null, etaAt: null, completedAt: null, failureMessage: null } });
          return;
        }
        reportZone();   // BUGS.md T14: the person's own day, from the zone their browser is on
        const store = me.stores.find((s) => s.kind === 'commercial') ?? me.stores[0];
        if (!store) { if (!cancelled) setPhase({ kind: 'no_store', me }); return; }
        const { state } = await api.onboardingState(store.tenantId);
        if (cancelled) return;
        setPhase({ kind: 'ready', me, store, state });
        setTick((t) => t + 1);
        if (state.status === 'connected' || state.status === 'backfilling') timer = window.setTimeout(load, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Unauthenticated) setPhase({ kind: 'signed_out' });
        else setPhase({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    };
    const unsubscribe = onViewElsewhere(() => {
      if (cancelled || checkingView) return;
      checkingView = true;
      void (async () => {
        try {
          const me = await api.me();
          if (cancelled || !me.viewing) return;
          sessionStorage.setItem(VIEW_MARKER, me.viewing.sessionId);
          await load();
        } catch {
          // The original refusal stays with its caller; a later notice can try again.
        } finally {
          checkingView = false;
        }
      })();
    });
    void load();
    return () => { cancelled = true; unsubscribe(); if (timer) window.clearTimeout(timer); };
  }, []);

  const connect = async (store: Store) => {
    const { state } = await api.connectMail(store.tenantId);
    setPhase((p) => (p.kind === 'ready' ? { ...p, state } : p));
    window.setTimeout(() => window.location.reload(), 1500);
  };

  switch (phase.kind) {
    case 'booting':
      return <main className="screen mc"><Logo size={44} spinning /></main>;
    case 'signed_out':
      return <SignIn/>;
    case 'no_store':
      return <main className="screen mc"><Logo size={44} /><p className="plain">{phase.me.waitingForInvite ? 'You have been added to Cobrain, and Mike has been told you are waiting for your invite.' : 'Your account is not in a Cobrain store yet. Ask your admin.'}</p><LogoutButton /></main>;
    case 'error':
      return <main className="screen mc"><Logo size={44} /><p className="plain">Cobrain could not load. Please try again in a moment.</p><LogoutButton /></main>;
    case 'ready': {
      const { store, state, me } = phase;
      if (window.location.pathname === '/copilot') return <AccessProvider me={me} store={store}><CopilotAccess me={me} /></AccessProvider>;
      if (me.viewing) return <AccessProvider me={me} store={store}><Shell me={me} store={store} surface={surface} go={go} /></AccessProvider>;
      // B73: the connector gate is the commercial store's. A personal store has no mailbox
      // of its own -- nothing is ever connected to it, and its items arrive by routing from
      // the commercial one (D147, R1/R2) -- so asking it to connect email is a door that
      // never opens.
      const gated = store.kind === 'commercial';
      // B2442 (3): the journey owns admission. Behind it, the shell no longer waits for the whole
      // backfill (D18's wait is replaced by the understood two weeks), so a person let in is never
      // thrown back to the loading screen while the rest of the month is read. What stays: the
      // mailbox never connected, and D98's stop on a failed mandatory connector, which the journey
      // does not re-gate for an active principal. BUGS.md X66: what the not-connected screen says is read is the onboarding
      // panel's own sentence (onboarding/Onboarding.tsx), under the brief's copy rule (section 5), which forbids a full read.
      const legacy = gated && state.status === 'not_connected' ? (
        <main className="screen mc">
          <Logo />
          <h1>Hello, {me.login.displayName.split(' ')[0]}.</h1>
          <p className="plain">First, Cobrain needs your email.</p>
          <p className="meta">Cobrain reads six months of your sent mail to learn how you write, then the last two weeks of everything so your first list is ready, then the rest of the month while you work. Nothing is sent and nothing in your mailbox changes.</p>
          <button className="button" onClick={() => void connect(store)}>Connect my email</button>
          <LogoutButton />
        </main>
      )
        : gated && state.status === 'failed' ? <><LogoutButton corner /><LoadingScreen state={state} tick={tick} /></>
        : <Shell me={me} store={store} surface={surface} go={go} />;
      return <AccessProvider me={me} store={store}>{gated ? <OnboardingGate me={me} store={store}>{legacy}</OnboardingGate> : legacy}</AccessProvider>;
    }
  }
}
